import React, { useState, useEffect } from "react";
import PageHeading from "../ui/PageHeading";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  deleteDoc,
  doc,
} from "../../config/dataClient";
import { History, ChevronLeft, ChevronRight } from "lucide-react";
import { db } from "../../config/firebase";
import { APP_ID } from "../../config/constants";
import {
  formatDate,
  formatTime,
  getDaysInMonth,
} from "../../utils/timeHelpers";

export default function HistoryView({ user }) {
  const [logs, setLogs] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());

  // 1. 抓取該用戶所有紀錄
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, "artifacts", APP_ID, "users", user.uid, "logs"),
      orderBy("timestamp", "asc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedLogs = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
          dateObj: doc.data().timestamp?.toDate(),
        }))
        .filter((l) => l.dateObj);
      setLogs(fetchedLogs);
    });

    return () => unsubscribe();
  }, [user]);

  const changeMonth = (delta) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + delta);
    setCurrentDate(newDate);
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const startDay = new Date(year, month, 1).getDay();

  const monthLogs = logs.filter(
    (l) => l.dateObj.getMonth() === month && l.dateObj.getFullYear() === year
  );

  return (
    <div className="paper-page history-view p-4 pb-24">
      <PageHeading icon={History}>打卡紀錄</PageHeading>

      <div className="lake-toolbar bg-white p-3 rounded-xl border border-slate-100 mb-4 flex justify-between items-center shadow-sm">
        <button
          onClick={() => changeMonth(-1)}
          aria-label="上個月" title="上個月"
          className="lake-icon-button p-2 hover:bg-slate-50 rounded-full text-slate-500 transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="font-bold text-slate-700 text-lg">
          {year}年 {month + 1}月
        </div>
        <button
          onClick={() => changeMonth(1)}
          aria-label="下個月" title="下個月"
          className="lake-icon-button p-2 hover:bg-slate-50 rounded-full text-slate-500 transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="calendar-card bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="calendar-weekdays grid grid-cols-7 bg-slate-50 border-b border-slate-200">
          {["日", "一", "二", "三", "四", "五", "六"].map((d) => (
            <div
              key={d}
              className="py-2 text-center text-xs font-bold text-slate-500"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="history-calendar-grid grid grid-cols-7 auto-rows-fr bg-slate-200 gap-px border-b border-slate-200">
          {Array.from({ length: startDay }).map((_, i) => (
            <div key={`empty-${i}`} className="calendar-empty bg-white min-h-[80px]"></div>
          ))}

          {daysInMonth.map((date) => {
            const dateStr = formatDate(date);
            const isToday = formatDate(new Date()) === dateStr;
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            const dayLogs = monthLogs.filter(
              (l) => formatDate(l.dateObj) === dateStr
            );

            return (
              <div
                key={dateStr}
                className={`calendar-day bg-white min-h-[80px] p-1 flex flex-col ${
                  isWeekend ? "bg-slate-50/50" : ""
                }`}
              >
                <span
                  className={`calendar-date text-xs font-bold mb-1 text-center w-5 h-5 rounded-full leading-5 self-center ${
                    isToday
                      ? "calendar-today bg-blue-600 text-white"
                      : isWeekend
                      ? "text-red-400"
                      : "text-slate-700"
                  }`}
                >
                  {date.getDate()}
                </span>

                <div className="flex flex-col gap-1 overflow-y-auto items-center">
                  {dayLogs.map((log) => (
                    // 修改：字體縮小、移除刪除按鈕、只顯示 HH:mm
                    <div
                      key={log.id}
                      className={`punch-chip flex justify-center items-center px-1 py-0.5 rounded border text-[10px] w-full font-mono font-bold ${
                        log.type === "in"
                          ? "punch-in bg-green-50 border-green-100 text-green-700"
                          : "punch-out bg-orange-50 border-orange-100 text-orange-700"
                      }`}
                    >
                      {formatTime(log.dateObj).slice(0, 5)}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex gap-4 text-xs text-slate-500 justify-center">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-50 border border-green-200 rounded"></div>
          <span>上班</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-orange-50 border border-orange-200 rounded"></div>
          <span>下班</span>
        </div>
      </div>
    </div>
  );
}
